// Generates a phone-wallpaper JPEG (1080×1920) of the race plan table.
// Columns: Segment | Δt | Start km | Elapsed
// Auto-scales row height + font so all segments always fit.

const fmtElapsed = (s) => {
  if (s == null) return '-';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}:${String(m).padStart(2, '0')}`;
};

// Duration, rounded to the nearest minute (unlike fmtElapsed, which floors).
const fmtDuration = (s) => {
  if (s == null) return '-';
  const totalMin = Math.round(s / 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
};

function clipText(ctx, text, maxWidth) {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let lo = 0, hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (ctx.measureText(text.slice(0, mid) + '…').width <= maxWidth) lo = mid;
    else hi = mid - 1;
  }
  return text.slice(0, lo) + '…';
}

// App grey palette (dark mode)
const G = {
  100: '#cfdcdf',
  200: '#bccdd2',
  300: '#a9bfc5',
  400: '#7f9399',
  500: '#6f8186',
  600: '#505c60',
  700: '#303739',
  800: '#202526',
  900: '#101213',
};

export function downloadPlanWallpaper(racePlanSegments, raceName) {
  const W = 1080;
  const H = 2340; // 9:19.5 — matches modern iPhone portrait (14, 15, Pro)
  const PAD = 32;
  const FOOTER_H = 44;
  const TITLE_H = 72;

  const SEG_W  = 530;
  const DUR_W  = 82;
  const KM_W   = 72;
  const GAIN_W = 72;
  const LOSS_W = 72;
  const CUM_W  = 86;
  const ELA_W  = 1016 - SEG_W - DUR_W - KM_W - GAIN_W - LOSS_W - CUM_W; // = 102

  let cx = PAD;
  const cols = [
    { label: 'Segment', x: cx,           w: SEG_W,  align: 'left'  },
    { label: 'Δt',      x: cx += SEG_W,  w: DUR_W,  align: 'right' },
    { label: 'End',     x: cx += DUR_W,  w: KM_W,   align: 'right' },
    { label: '+m',      x: cx += KM_W,   w: GAIN_W, align: 'right' },
    { label: '-m',      x: cx += GAIN_W, w: LOSS_W, align: 'right' },
    { label: 'ΣD+',x: cx += LOSS_W, w: CUM_W,  align: 'right' },
    { label: 'Elapsed', x: cx += CUM_W,  w: ELA_W,  align: 'right' },
  ];

  const HEADER_H = 68;
  const availableForRows = H - TITLE_H - HEADER_H - FOOTER_H;
  const ROW_H       = Math.max(26, Math.floor(availableForRows / racePlanSegments.length));
  // Cap font size so a short (e.g. minimal-mode) table with few, tall rows
  // doesn't grow text past what the fixed column widths can fit.
  const FONT_SIZE   = Math.min(24, Math.max(18, Math.round(ROW_H * 0.44)));
  const HEADER_FONT = Math.min(28, Math.max(20, Math.round(ROW_H * 0.50)));
  const TITLE_FONT  = 38;

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // ── Background ─────────────────────────────────────────────────────────────
  ctx.fillStyle = G[900];
  ctx.fillRect(0, 0, W, H);

  // ── Title ──────────────────────────────────────────────────────────────────
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.font = `bold ${TITLE_FONT}px Arial, sans-serif`;
  ctx.fillStyle = G[100];
  ctx.fillText(raceName || 'Race Plan', PAD, TITLE_H / 2);

  // Accent line under title
  ctx.fillStyle = G[500];
  ctx.fillRect(PAD, TITLE_H - 5, 100, 2);

  // ── Table header ───────────────────────────────────────────────────────────
  const tableTop = TITLE_H;
  ctx.fillStyle = G[700];
  ctx.fillRect(PAD - 6, tableTop, W - 2 * (PAD - 6), HEADER_H);

  ctx.font = `bold ${HEADER_FONT}px Arial, sans-serif`;
  ctx.fillStyle = G[100];
  for (const col of cols) {
    ctx.textAlign = col.align;
    const tx = col.align === 'right' ? col.x + col.w - 8 : col.x + 8;
    ctx.fillText(col.label, tx, tableTop + HEADER_H / 2);
  }

  // ── Rows ───────────────────────────────────────────────────────────────────
  let y = tableTop + HEADER_H;
  let cumGain = 0;

  for (let i = 0; i < racePlanSegments.length; i++) {
    const seg = racePlanSegments[i];
    const isStop = seg.segment_type === 'checkpoint';
    if (!isStop) cumGain += (seg.elevation_gain_m || 0);

    ctx.fillStyle = isStop ? G[700] : (i % 2 === 0 ? G[900] : G[800]);
    ctx.fillRect(PAD - 6, y, W - 2 * (PAD - 6), ROW_H);

    // Left accent stripe for stops
    if (isStop) {
      ctx.fillStyle = G[400];
      ctx.fillRect(PAD - 6, y, 3, ROW_H);
    }

    ctx.fillStyle = isStop ? G[200] : G[300];
    ctx.font = isStop
      ? `bold ${FONT_SIZE}px Arial, sans-serif`
      : `${FONT_SIZE}px Arial, sans-serif`;

    const segLabel = isStop
      ? `⏸ ${seg.start_label}`
      : `${seg.start_label} → ${seg.end_label}`;
    const duration = fmtDuration(seg.duration_s);
    const endKm  = seg.end_distance_m != null ? (seg.end_distance_m / 1000).toFixed(1) : '-';
    const gain   = isStop ? '' : (seg.elevation_gain_m ? `+${Math.round(seg.elevation_gain_m)}` : '-');
    const loss   = isStop ? '' : (seg.elevation_loss_m ? `-${Math.round(seg.elevation_loss_m)}` : '-');
    const cumD   = cumGain > 0 ? `+${Math.round(cumGain)}` : '-';
    const elapsed = fmtElapsed(seg.elapsed_s);

    const rowData = [segLabel, duration, endKm, gain, loss, cumD, elapsed];
    const cy = y + ROW_H / 2;

    for (let c = 0; c < cols.length; c++) {
      const col = cols[c];
      ctx.textAlign = col.align;
      const tx = col.align === 'right' ? col.x + col.w - 8 : col.x + 8;
      const text = c === 0 ? clipText(ctx, rowData[c], col.w - 16) : rowData[c];
      ctx.fillText(text, tx, cy);
    }

    y += ROW_H;
  }

  // Bottom border line
  ctx.fillStyle = G[600];
  ctx.fillRect(PAD - 6, y, W - 2 * (PAD - 6), 1);

  // ── Footer ─────────────────────────────────────────────────────────────────
  ctx.font = `${Math.round(FOOTER_H * 0.5)}px Arial, sans-serif`;
  ctx.fillStyle = G[500];
  ctx.textAlign = 'center';
  ctx.fillText('mkpace.com', W / 2, H - FOOTER_H / 2);

  // ── Download ───────────────────────────────────────────────────────────────
  canvas.toBlob((blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(raceName || 'race-plan').replace(/\s+/g, '-').toLowerCase()}-wallpaper.jpg`;
    a.click();
    URL.revokeObjectURL(url);
  }, 'image/jpeg', 0.93);
}
